import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationService } from '../../../core/navigation/navigation.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { VexConfigService } from '@vex/config/vex-config.service';
import { filter, map, startWith } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationItem } from '../../../core/navigation/navigation-item.interface';
import { VexPopoverService } from '@vex/components/vex-popover/vex-popover.service';
import { Observable, combineLatest, of, take } from 'rxjs';
import { SidenavUserMenuComponent } from './sidenav-user-menu/sidenav-user-menu.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SearchModalComponent } from './search-modal/search-modal.component';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';
import { VexScrollbarComponent } from '@vex/components/vex-scrollbar/vex-scrollbar.component';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe, NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { VexPopoverRef } from '@vex/components/vex-popover/vex-popover-ref';
import { AuthStore } from '@core/services/auth/auth.store';
import {
  ContextualNavigationItem,
  EntityContext
} from '../../../shared/services/contextual-sidebar.service';
import {
  ContextualSidebarService
} from '../../../shared/services/contextual-sidebar.service';
import { WorkspaceStore } from '../../../shared/services/workspace.store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { contextualGroupsContaining, isContextualItemActiveOn } from './contextual-groups';

/** Un simple passage de souris sur le rail ne doit rien déployer. */
export const SIDENAV_HOVER_OPEN_DELAY_MS = 120;
/** Ni un écart de souris d'un instant replier le panneau sous le curseur. */
export const SIDENAV_HOVER_CLOSE_DELAY_MS = 180;

@Component({
  selector: 'vex-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatRippleModule,
    SidenavItemComponent,
    VexScrollbarComponent,
    NgClass,
    AsyncPipe,
    RouterModule,
    MatTooltipModule,
    TranslatePipe
  ]
})
export class SidenavComponent {
  readonly collapsed = input(false);

  private readonly navigationService = inject(NavigationService);
  private readonly layoutService = inject(VexLayoutService);
  private readonly configService = inject(VexConfigService);
  private readonly popoverService = inject(VexPopoverService);
  private readonly dialog = inject(MatDialog);
  private readonly authStore = inject(AuthStore);
  private readonly contextualSidebarService = inject(ContextualSidebarService);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** Backend seed name for a freshly-created personal workspace. */
  private static readonly SEED_PERSONAL_NAME = 'Private Workspace';

  collapsedOpen$ = this.layoutService.sidenavCollapsedOpen$;
  pinned$ = this.layoutService.sidenavPinned$;
  /**
   * L'épingle s'affiche partout où le rail existe, épinglée ou non. La gater
   * sur l'état épinglé la faisait disparaître au rechargement d'une session
   * épinglée : le menu restait déplié et plus rien ne permettait de le rendre
   * au rail.
   */
  railCapable$ = this.layoutService.sidenavRailCapable$;
  title$ = this.configService.config$.pipe(map(config => config.sidenav.title));
  imageUrl$ = this.configService.config$.pipe(map(config => config.sidenav.imageUrl));
  userVisible$ = this.configService.config$.pipe(map(config => config.sidenav.user.visible));
  searchVisible$ = this.configService.config$.pipe(map(config => config.sidenav.search.visible));

  userMenuOpen$: Observable<boolean> = of(false);

  items$: Observable<NavigationItem[]> = this.navigationService.items$;

  contextualItems$: Observable<ContextualNavigationItem[]> = this.contextualSidebarService.navigationItems$;
  contextualContext$: Observable<EntityContext | null> = this.contextualSidebarService.context$;
  showContextual$: Observable<boolean> = this.contextualSidebarService.isVisible$;

  readonly user = this.authStore.user;

  popoverRef: VexPopoverRef | undefined;

  private pointerInside = false;
  private focusInside = false;
  private hoverTimer: number | undefined;
  /** `url|groupes` du dernier passage de `expandGroupOfActivePage`. */
  private lastAutoExpandKey = '';

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearHoverTimer());

    const url$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    );

    combineLatest([url$, this.contextualItems$])
      .pipe(takeUntilDestroyed())
      .subscribe(([url, items]) => {
        this.currentUrl.set(url);
        this.expandGroupOfActivePage(url, items);
      });
  }

  /** URL affichée, pour les entrées contextuelles à routes secondaires. */
  private readonly currentUrl = signal(this.router.url);

  /** `active` posé à la main, ou page affichée sous une `alsoActiveOn`. */
  isContextualItemActive(item: ContextualNavigationItem): boolean {
    return isContextualItemActiveOn(item, this.currentUrl());
  }

  // Contextual navigation expansion state
  private readonly contextualExpandedItems = signal<Set<string>>(
    new Set(['marketing', 'configuration', 'data-management', 'analytics', 'system-tools'])
  );

  trackByRoute(index: number, item: NavigationItem): string {
    if (item.type === 'link') {
      return item.route as string;
    }
    // Divider/subheading entries often carry an empty `label` (e.g. the
    // property-manager nav has three unlabelled subheadings) — falling back
    // to `item.label` alone collides them under the same `""` track key,
    // which Angular then endlessly recreates (NG0955/NG0956).
    return item.label ? `${item.type}:${item.label}` : `${item.type}:${index}`;
  }

  readonly userName = computed(() => {
    const u = this.user();
    if (!u) return 'User';
    if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
    if (u.firstName) return u.firstName;
    if (u.email) return u.email.split('@')[0];
    return 'User';
  });

  get workspaceName(): string {
    const currentWorkspace = this.workspaceStore.currentWorkspace();
    if (!currentWorkspace) {
      return 'No Workspace';
    }

    // Honour the name the owner chose during onboarding / in settings. Only
    // fall back to a localized "personal" label when it's still the backend
    // seed default ('Private Workspace') or empty — previously this branch
    // hard-coded "Personal Workspace" and swallowed the user's chosen name.
    const name = currentWorkspace.name?.trim();
    if (!name || name === SidenavComponent.SEED_PERSONAL_NAME) {
      return this.translate.instant('workspace.personalDefault');
    }
    return name;
  }

  get isPersonalWorkspace(): boolean {
    const currentWorkspace = this.workspaceStore.currentWorkspace();
    return currentWorkspace?.type === 'personal';
  }

  get isServiceProviderWorkspace(): boolean {
    const currentWorkspace = this.workspaceStore.currentWorkspace();
    return currentWorkspace?.type === 'service_provider';
  }

  /**
   * Le panneau se déploie à la volée de la souris et se replie quand elle part,
   * mais jamais au premier pixel : deux minuteries filtrent le geste. Le rail
   * reste ouvert tant que quelque chose l'occupe — la souris, le focus clavier,
   * ou le menu profil, qui est un overlay ancré sur le panneau et se recalerait
   * sur une origine de 72px si on repliait sous lui.
   */
  onSidenavMouseEnter(): void {
    this.pointerInside = true;
    this.scheduleHoverOpen();
  }

  onSidenavMouseLeave(): void {
    this.pointerInside = false;
    this.scheduleHoverCloseIfIdle();
  }

  /**
   * Branché sur `focusin`/`focusout`, qui remontent. `focusout` se déclenche à
   * chaque saut d'un item à l'autre, mais c'est sans conséquence : la fermeture
   * passe par une minuterie de 180 ms que le `focusin` suivant annule. Sans
   * cela le rail replié serait inutilisable au clavier, seules les infobulles
   * restant pour deviner où l'on est.
   */
  onSidenavFocusChange(focused: boolean): void {
    this.focusInside = focused;

    if (focused) {
      this.scheduleHoverOpen();
    } else {
      this.scheduleHoverCloseIfIdle();
    }
  }

  togglePin(): void {
    this.layoutService.toggleSidenavPin();
    this.clearHoverTimer();

    // Dépingler rend le panneau au rail alors que le curseur est encore dessus.
    // Sans ce rappel il se replierait sous la souris, et aucun `mouseleave` ne
    // viendrait jamais le rouvrir puisqu'on n'en est jamais sorti.
    if (this.pointerInside || this.focusInside) {
      this.layoutService.collapseOpenSidenav();
    }
  }

  private scheduleHoverOpen(): void {
    // Sous 960px le tiroir est en mode `over` : `collapsed` y vaut `false` et
    // la souris n'a rien à y piloter.
    if (!this.collapsed()) {
      return;
    }

    this.clearHoverTimer();
    this.hoverTimer = window.setTimeout(() => {
      this.hoverTimer = undefined;
      this.layoutService.collapseOpenSidenav();
    }, SIDENAV_HOVER_OPEN_DELAY_MS);
  }

  private scheduleHoverCloseIfIdle(): void {
    if (!this.collapsed() || this.pointerInside || this.focusInside) {
      return;
    }

    this.clearHoverTimer();

    if (this.popoverRef) {
      return;
    }

    this.hoverTimer = window.setTimeout(() => {
      this.hoverTimer = undefined;
      this.layoutService.collapseCloseSidenav();
    }, SIDENAV_HOVER_CLOSE_DELAY_MS);
  }

  private clearHoverTimer(): void {
    if (this.hoverTimer !== undefined) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }

  openProfileMenu(origin: HTMLDivElement): void {
    if (this.popoverRef) {
      this.popoverRef.close();
    }

    this.popoverRef = this.popoverService.open({
      content: SidenavUserMenuComponent,
      origin,
      offsetY: -8,
      width: origin.clientWidth,
      position: [
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom'
        }
      ]
    });

    this.userMenuOpen$ = this.popoverRef.afterClosed$.pipe(
      map(closed => !closed),
      startWith(true)
    );

    this.popoverRef.afterClosed$.pipe(take(1)).subscribe(() => {
      this.popoverRef = undefined;
      // La souris est peut-être déjà partie pendant que le menu était ouvert :
      // le repli qu'on avait refusé alors se rejoue maintenant.
      this.scheduleHoverCloseIfIdle();
    });
  }

  openSearch(): void {
    this.dialog.open(SearchModalComponent, {
      panelClass: 'vex-dialog-glossy',
      width: '100%',
      maxWidth: '600px'
    });
  }

  // Contextual navigation methods
  trackByContextualRoute(index: number, item: ContextualNavigationItem): string {
    return item.id;
  }

  onContextualItemClick(item: ContextualNavigationItem): void {
    if (item.clickHandler) {
      item.clickHandler();
    }
    // Navigation will be handled by routerLink in template
  }

  onBackClick(): void {
    // Hide the contextual sidebar and navigate back
    this.contextualSidebarService.hideSidebar();
    void this.contextualSidebarService.navigateBack();
  }

  resetNavigation(): void {
    this.contextualSidebarService.reset();
    void this.router.navigate(['/']);
  }

  getEntityIcon(type: string): string {
    switch (type) {
      case 'property': return 'mat:home';
      case 'contract': return 'mat:description';
      case 'tenant': return 'mat:person';
      case 'loan': return 'mat:account_balance';
      case 'building': return 'mat:apartment';
      case 'assembly': return 'mat:how_to_vote';
      case 'repair': return 'mat:build';
      case 'maintenance': return 'mat:handyman';
      case 'investment-calculator': return 'mat:bar_chart';
      case 'admin': return 'mat:admin_panel_settings';
      case 'help': return 'mat:help_outline';
      case 'account-settings': return 'mat:settings';
      case 'communication': return 'mat:email';
      default: return 'mat:help';
    }
  }

  // Contextual navigation expansion methods
  toggleContextualExpanded(itemId: string): void {
    this.contextualExpandedItems.update(currentExpanded => {
      const newExpanded = new Set(currentExpanded);

      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }

      return newExpanded;
    });
  }

  /**
   * Sur une sous-page (Caractéristiques, Contrats, Syndic…), ouvrir le groupe
   * qui la contient : fermé, il cachait la page active et l'aside n'allumait
   * rien, ni au rail ni déplié. On ajoute, on ne retire jamais, et on ne rejoue
   * qu'à un changement d'URL ou de groupes : les items ré-émettent à chaque
   * pastille rafraîchie, et un groupe replié à la main doit le rester tant
   * qu'on ne navigue pas.
   */
  private expandGroupOfActivePage(url: string, items: ContextualNavigationItem[]): void {
    const groupIds = contextualGroupsContaining(items, url);
    const key = `${url}|${groupIds.join(',')}`;

    if (key === this.lastAutoExpandKey) {
      return;
    }
    this.lastAutoExpandKey = key;

    if (groupIds.length === 0) {
      return;
    }

    this.contextualExpandedItems.update(
      currentExpanded => new Set([...currentExpanded, ...groupIds])
    );
  }

  isContextualExpanded(itemId: string): boolean {
    return this.contextualExpandedItems().has(itemId);
  }
}
